import { Divider, Modal } from "antd";
import _ from "lodash";
import moment from "moment";
import React, { ComponentType, FC, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { OUTPUT_FORMAT, TIME_INPUT_FORMAT } from "../../helpers";
import { Location } from "../../queries/auto-generated/schemas";
import { LOCATIONS_PAGE_PATH } from "../../router";
import Address from "./address";
import Phone from "./phone";

interface WithMoreInfoProps {
  location: Location;
}

/**
 * HOC for adding a click handler to display a location info modal
 * @param Component
 */
const withMoreInfo = <P extends Object>(
  Component: ComponentType<P>
): FC<P & WithMoreInfoProps> => ({ location, ...props }) => {
  const [visible, updateVisibility] = useState(false);
  return (
    <>
      <Component onClick={() => updateVisibility(true)} {...(props as P)}>
        {props.children}
      </Component>
      <MoreInfoModal
        location={location}
        visible={visible}
        updateVisibility={updateVisibility}
      />
    </>
  );
};

const MoreInfoModal: FC<{
  location: Location;
  visible: boolean;
  updateVisibility: Function;
}> = ({ location, visible, updateVisibility }) => {
  const browserLocation = useLocation();

  const onLocationsPage = browserLocation.pathname === LOCATIONS_PAGE_PATH;
  return (
    <StyledModal
      visible={visible}
      title={location.business_name}
      onCancel={() => updateVisibility(false)}
      footer={null}
      destroyOnClose={false}
    >
      {location.address && <Address address={location.address} />}
      {location.phone_number && <Phone phoneNumber={location.phone_number} />}
      {!onLocationsPage && (
        <StyledLink to={LOCATIONS_PAGE_PATH}>Switch locations</StyledLink>
      )}
      <Divider>Open hours</Divider>
      <div>
        <FormattedHours location={location} />
      </div>
    </StyledModal>
  );
};

const FormattedHours: FC<{ location: Location }> = ({ location }) => {
  const periods = location.business_hours?.periods ?? [];

  const days = _.groupBy(periods, "day_of_week");

  const dayStrings = _.map(days, (day, name) => {
    return `${name} : ${day
      .map((period) => {
        const start = moment(
          period?.start_local_time,
          TIME_INPUT_FORMAT
        ).format(OUTPUT_FORMAT);
        const end = moment(period?.end_local_time, TIME_INPUT_FORMAT).format(
          OUTPUT_FORMAT
        );
        return `${start} - ${end}`;
      })
      .join(" | ")}`;
  });

  return (
    <StyledHours>
      <ul>
        {dayStrings.map((day, index) => (
          <li key={index}>{day}</li>
        ))}
      </ul>
    </StyledHours>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-title,
  .ant-modal-body {
    text-align: center;
  }
`;

const StyledLink = styled(Link)`
  color: rgba(0, 0, 0, 0.85);
  text-decoration: underline;
  text-decoration-color: #ffeb19;
`;

const StyledHours = styled.div`
  ul {
    list-style-type: none;
    padding-left: 0;
  }
`;
export default withMoreInfo;
